import React, { useState } from 'react';
import { Modal, Upload, Button, notification, Spin } from 'antd';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { bulkUploadStockService } from '../../../services/stock-services';

const BulkUploadModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  onDownloadTemplate 
}) => {
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(0); // 0: select file, 1: uploading, 2: ready to update, 3: processing

  const handleFileUpload = (file) => {
    setUploadFile(file);
    setUploadStep(1);
    setIsUploading(true);
    setUploadProgress(0);
    
    // Simulate file upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setIsUploading(false);
          setUploadStep(2); // Move to "ready to update" step
          return 100;
        }
        return prev + 10;
      });
    }, 200);
    
    return false; // Prevent default upload
  };

  const handleUpdate = async () => {
    setUploadStep(3); // Move to processing step
    
    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('document', uploadFile);

      // Call API
      const response = await bulkUploadStockService(formData);

      if (response.status === 200 || response.data) {
        console.log('Bulk upload response:', response.data);
        
        notification.success({
          message: response.data.message || 'File processed successfully',
        });

        // Move to result screen
        setTimeout(() => {
          handleClose();
          onSuccess(response.data.data);
        }, 500);
      }
    } catch (error) {
      notification.error({
      description: error.response?.data?.message ?? error?.message,
      });
      // Reset to file selection
      resetStates();
    }
  };

  const handleCancelUpload = () => {
    resetStates();
  };

  const handleClose = () => {
    resetStates();
    onClose();
  };

  const resetStates = () => {
    setUploadStep(0);
    setUploadFile(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  return (
    <>
      {/* Upload Modal - Step 0: File Selection */}
      <Modal 
        open={isOpen && uploadStep === 0} 
        onCancel={handleClose}
        footer={null}
        title='Bulk Update'
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Please upload the bulk update file in the standard format. Please find the <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={onDownloadTemplate}>sample file here</span>
          </p>
          <Upload.Dragger 
            beforeUpload={handleFileUpload} 
            multiple={false} 
            accept='.xls,.xlsx'
            showUploadList={false}
          >
            <p className='ant-upload-drag-icon' style={{ fontSize: '48px', color: '#1890ff' }}>
              <UploadOutlined />
            </p>
            <p className='ant-upload-text' style={{ fontSize: '16px', marginBottom: '8px' }}>
              Supported format: XLS & XLSXOnly
            </p>
            <p className='ant-upload-hint' style={{ color: '#1890ff', cursor: 'pointer' }}>
              Browse file
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* Upload Modal - Step 1: Upload Progress */}
      <Modal 
        open={isOpen && uploadStep === 1} 
        onCancel={isUploading ? null : handleCancelUpload}
        footer={null}
        title='Bulk Update'
        width={500}
        closable={!isUploading}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Uploading data, Please wait or cancel upload
          </p>
          <div style={{ margin: '40px 0' }}>
            <UploadOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
          </div>
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <strong>{uploadFile?.name}</strong>
          </div>
          <div style={{ marginBottom: '16px', textAlign: 'left', color: '#666' }}>
            {uploadFile ? (uploadFile.size / 1024).toFixed(2) : '0'} kb
          </div>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div 
              style={{ 
                width: `${uploadProgress}%`, 
                height: '8px', 
                backgroundColor: '#1890ff',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <div style={{ textAlign: 'left', color: '#666', fontSize: '12px' }}>
            {uploadProgress}%
          </div>
          {isUploading && (
            <Button 
              onClick={handleCancelUpload} 
              style={{ marginTop: '20px' }}
            >
              Cancel
            </Button>
          )}
        </div>
      </Modal>

      {/* Upload Modal - Step 2: Ready to Update */}
      <Modal 
        open={isOpen && uploadStep === 2} 
        onCancel={handleClose}
        footer={[
          <Button key='cancel' onClick={handleClose}>
            Cancel
          </Button>,
          <Button key='update' type='primary' onClick={handleUpdate}>
            Update
          </Button>
        ]}
        title='Bulk Update'
        width={500}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            File Uploaded. Click Update to update stock or click Cancel
          </p>
          <div style={{ margin: '40px 0' }}>
            <UploadOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
          </div>
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <strong>{uploadFile?.name}</strong>
          </div>
          <div style={{ marginBottom: '16px', textAlign: 'left', color: '#666' }}>
            {uploadFile ? (uploadFile.size / 1024).toFixed(2) : '0'} kb
          </div>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div 
              style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#52c41a',
              }}
            />
          </div>
          <div style={{ textAlign: 'left', color: '#52c41a', fontSize: '12px' }}>
            ✓ Upload Complete
          </div>
        </div>
      </Modal>

      {/* Upload Modal - Step 3: Processing */}
      <Modal 
        open={isOpen && uploadStep === 3} 
        footer={null}
        title='Bulk Update'
        width={500}
        closable={false}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Updating data, Please wait or cancel upload
          </p>
          <div style={{ margin: '40px 0' }}>
            <UploadOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
          </div>
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <strong>{uploadFile?.name}</strong>
          </div>
          <div style={{ marginBottom: '16px', textAlign: 'left', color: '#666' }}>
            {uploadFile ? (uploadFile.size / 1024).toFixed(2) : '0'} kb
          </div>
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BulkUploadModal;












